/**
 * ระบบคำนวณค่าหอพักและยอดคงค้าง (Dormitory Fee & Balance Calculation Engine)
 * โรงพยาบาลโรคผิวหนังเขตร้อนภาคใต้ จังหวัดตรัง
 *
 * กฎการคำนวณ (Business Rules):
 * 1. ตึก 1, 3, 4 (นักศึกษาฝึกงาน / บุคลากร):
 *    - ค่าใช้จ่ายตั้งต้นรายเดือน (4 สัปดาห์ / 28 วัน) = 500.00 บาท
 *    - ค่าใช้จ่ายรายสัปดาห์ = 500 / 4 = 125.00 บาท
 *    - ค่าใช้จ่ายรายวัน = 125 / 7 = 17.85714... บาท (ประมาณ 17.86 บาท/วัน)
 *    - รวมยอดที่ต้องจ่าย = (จำนวนเดือน x 500) + (สัปดาห์ที่เหลือ x 125) + (วันที่เหลือ x 17.86)
 *      เช่น 1 เดือน + 1 สัปดาห์ = 500 + 125 = 625.00 บาท
 *
 * 2. ตึก 2 (แพทย์ fellow / elective):
 *    - อัตราค่าบริการรายวัน = 100.00 บาท/คน/วัน
 *    - รวมยอดที่ต้องจ่าย = จำนวนวันเข้าพักจริง x 100 บาท
 *      เช่น 14 วัน = 1,400.00 บาท
 */

export const DEFAULT_RATES = {
  building1_3_4: {
    monthly: 500, // บาท / เดือน (4 สัปดาห์)
    weekly: 125, // บาท / สัปดาห์ (500 / 4)
    daily: 17.86 // บาท / วัน (125 / 7)
  },
  building2_doctor: {
    daily: 100 // บาท / คน / วัน สำหรับแพทย์ elective
  },
  BUILDING_1_3_4: {
    MONTHLY: 500,
    WEEKLY: 125,
    DAILY: 17.86
  },
  BUILDING_2_DOCTOR: {
    DAILY_RATE: 100,
    DAILY: 100
  }
};

/**
 * คำนวณจำนวนวันระหว่างวันที่ 2 วัน (รวมวันเริ่มต้นและสิ้นสุด)
 */
export function countDaysBetween(startDateStr, endDateStr) {
  if (!startDateStr || !endDateStr) return 0;
  const start = new Date(startDateStr);
  const end = new Date(endDateStr);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
}

/**
 * คำนวณค่าหอพักแบบ Proration (เดือน, สัปดาห์, วัน)
 * @param {string} building - เช่น "ตึก 1", "ตึก 2", "ตึก 3", "ตึก 4"
 * @param {number} totalDays - จำนวนวันพักทั้งหมด
 * @param {object} customRates - เรทราคาที่ผู้ใช้กำหนด (ถ้ามี)
 * @returns {object} ผลการคำนวณและรายละเอียดแจกแจง
 */
export function calculateDormFeeByDays(building, totalDays, customRates = DEFAULT_RATES) {
  const isBuilding2 = building && (building.includes("2") || building.includes("แพทย์"));

  if (isBuilding2) {
    const dailyRate = customRates?.building2_doctor?.daily || 100;
    const totalAmount = totalDays * dailyRate;
    return {
      buildingType: "ตึก 2 (แพทย์ Elective)",
      rateType: "DAILY_100",
      totalDays,
      months: 0,
      weeks: 0,
      days: totalDays,
      breakdownText: `${totalDays} วัน × ${dailyRate} บาท/วัน`,
      totalAmount: Math.round(totalAmount * 100) / 100
    };
  }

  // ตึก 1, 3, 4 (สูตร 1 เดือน = 4 สัปดาห์ = 28 วัน)
  const monthlyRate = customRates?.building1_3_4?.monthly || 500;
  const weeklyRate = customRates?.building1_3_4?.weekly || Math.round((monthlyRate / 4) * 100) / 100;
  const dailyRate = customRates?.building1_3_4?.daily || Math.round((weeklyRate / 7) * 100) / 100;

  const daysPerMonth = 28; // 4 สัปดาห์
  const daysPerWeek = 7;

  const months = Math.floor(totalDays / daysPerMonth);
  const remainingAfterMonths = totalDays % daysPerMonth;
  const weeks = Math.floor(remainingAfterMonths / daysPerWeek);
  const days = remainingAfterMonths % daysPerWeek;

  const monthAmount = months * monthlyRate;
  const weekAmount = weeks * weeklyRate;
  const dayAmount = Math.round(days * dailyRate * 100) / 100;
  const totalAmount = Math.round((monthAmount + weekAmount + dayAmount) * 100) / 100;

  // สร้างข้อความอธิบายแจกแจง
  const parts = [];
  if (months > 0) parts.push(`${months} เดือน (${monthAmount.toLocaleString()} บ.)`);
  if (weeks > 0) parts.push(`${weeks} สัปดาห์ (${weekAmount.toLocaleString()} บ.)`);
  if (days > 0) parts.push(`${days} วัน (${dayAmount.toLocaleString()} บ.)`);
  if (parts.length === 0) parts.push("0 วัน (0 บ.)");

  return {
    buildingType: building || "ตึก 1, 3, 4",
    rateType: "MONTHLY_500_PRORATED",
    totalDays,
    months,
    weeks,
    days,
    monthAmount,
    weekAmount,
    dayAmount,
    breakdownText: parts.join(" + "),
    totalAmount
  };
}

/**
 * คำนวณยอดคงค้าง และสถานะการชำระเงิน
 * @param {number} feeAmount - ยอดที่ต้องชำระทั้งหมด
 * @param {number} paidAmount - ยอดที่ชำระมาแล้ว
 * @returns {object}
 */
export function calculatePaymentBalance(feeAmount = 0, paidAmount = 0) {
  const totalDue = Number(feeAmount) || 0;
  const paid = Number(paidAmount) || 0;
  const remaining = Math.round((totalDue - paid) * 100) / 100;

  let status = "UNPAID";
  let statusText = "ค้างชำระ";
  let badgeColor = "danger";

  if (remaining <= 0) {
    status = "PAID";
    statusText = "ชำระครบแล้ว";
    badgeColor = "success";
  } else if (paid > 0 && remaining > 0) {
    status = "PARTIAL";
    statusText = `จ่ายบางส่วน (ค้าง ${remaining.toLocaleString()} บ.)`;
    badgeColor = "warning";
  }

  return {
    totalDue,
    paidAmount: paid,
    remainingBalance: Math.max(0, remaining),
    overpaidAmount: remaining < 0 ? Math.abs(remaining) : 0,
    status,
    statusText,
    badgeColor,
    isFullyPaid: remaining <= 0
  };
}

/**
 * เดือนในปีงบประมาณไทย 12 เดือน (ต.ค. - ก.ย.)
 */
export const THAI_FISCAL_MONTHS = [
  { key: "10", name: "ต.ค.", fullName: "ตุลาคม", offset: 0 },
  { key: "11", name: "พ.ย.", fullName: "พฤศจิกายน", offset: 1 },
  { key: "12", name: "ธ.ค.", fullName: "ธันวาคม", offset: 2 },
  { key: "01", name: "ม.ค.", fullName: "มกราคม", offset: 3 },
  { key: "02", name: "ก.พ.", fullName: "กุมภาพันธ์", offset: 4 },
  { key: "03", name: "มี.ค.", fullName: "มีนาคม", offset: 5 },
  { key: "04", name: "เม.ย.", fullName: "เมษายน", offset: 6 },
  { key: "05", name: "พ.ค.", fullName: "พฤษภาคม", offset: 7 },
  { key: "06", name: "มิ.ย.", fullName: "มิถุนายน", offset: 8 },
  { key: "07", name: "ก.ค.", fullName: "กรกฎาคม", offset: 9 },
  { key: "08", name: "ส.ค.", fullName: "สิงหาคม", offset: 10 },
  { key: "09", name: "ก.ย.", fullName: "กันยายน", offset: 11 }
];

/**
 * ตรวจสอบว่าช่วงข้อความวันที่เข้าพัก อยู่ในเดือนใดบ้าง
 * @param {string} dateRangeText - เช่น "3 พ.ย.-16 พ.ย. 2568" หรือ "1 ส.ค. 68 - 31 ก.ค. 69"
 * @param {Array<string>} explicitMonths - รายชื่อเดือนที่ระบุไว้ เช่น ["พ.ย."]
 * @returns {Set<string>} รายชื่อเดือน เช่น Set {"ต.ค.", "พ.ย."}
 */
export function extractActiveMonths(dateRangeText = "", explicitMonths = []) {
  const activeMonths = new Set(explicitMonths || []);
  if (!dateRangeText) return activeMonths;

  THAI_FISCAL_MONTHS.forEach((m) => {
    if (dateRangeText.includes(m.name) || dateRangeText.includes(m.fullName)) {
      activeMonths.add(m.name);
    }
  });

  return activeMonths;
}

/**
 * ตารางแปลงชื่อเดือนภาษาไทยเป็น index 0-11
 */
export const THAI_MONTH_INDEX_MAP = {
  "ม.ค.": 0, "ก.พ.": 1, "มี.ค.": 2, "เม.ย.": 3, "พ.ค.": 4, "มิ.ย.": 5,
  "ก.ค.": 6, "ส.ค.": 7, "ก.ย.": 8, "ต.ค.": 9, "พ.ย.": 10, "ธ.ค.": 11,
  "มกราคม": 0, "กุมภาพันธ์": 1, "มีนาคม": 2, "เมษายน": 3, "พฤษภาคม": 4, "มิถุนายน": 5,
  "กรกฎาคม": 6, "สิงหาคม": 7, "กันยายน": 8, "ตุลาคม": 9, "พฤศจิกายน": 10, "ธันวาคม": 11
};

/**
 * แปลงวันที่สิ้นสุดจากข้อความช่วงเวลาภาษาไทย เช่น "12 -27 ก.ย. 2569", "1 มิ.ย. - 8 ก.ย. 2569"
 * @param {string} text - ข้อความช่วงวันที่
 * @param {number} fallbackYear - ปี พ.ศ. สำรอง เช่น 2569
 * @returns {Date|null}
 */
export function parseThaiEndDate(text, fallbackYear = 2569) {
  if (!text) return null;
  const parts = text.split(/[-–]/);
  const endPart = (parts.length > 1 ? parts[parts.length - 1] : parts[0]).trim();

  // รองรับรูปแบบ dd/MM/yyyy
  if (endPart.includes("/")) {
    const slashParts = endPart.split("/").map(p => parseInt(p.trim(), 10));
    if (slashParts.length === 3 && !isNaN(slashParts[0]) && !isNaN(slashParts[1]) && !isNaN(slashParts[2])) {
      let d = slashParts[0];
      let m = slashParts[1] - 1;
      let y = slashParts[2];
      if (y > 2400) y -= 543;
      return new Date(y, m, d, 23, 59, 59, 999);
    }
  }

  const tokens = endPart.split(/\s+/).filter(Boolean);
  let day, monthName, year;

  for (const t of tokens) {
    const num = parseInt(t, 10);
    if (!isNaN(num)) {
      if (num > 2400) {
        year = num - 543;
      } else if (num >= 50 && num <= 99) {
        // ปี พ.ศ. 2 หลัก เช่น 68, 69 -> 2568, 2569
        year = (2500 + num) - 543;
      } else if (num > 2000 && num < 2200) {
        year = num;
      } else if (!day && num >= 1 && num <= 31) {
        day = num;
      }
    }
    for (const m of Object.keys(THAI_MONTH_INDEX_MAP)) {
      if (t.includes(m)) {
        monthName = m;
      }
    }
  }

  if (!year && fallbackYear) {
    year = fallbackYear > 2400 ? fallbackYear - 543 : fallbackYear;
  }
  if (!year) {
    year = new Date().getFullYear();
  }

  if (day && monthName !== undefined) {
    return new Date(year, THAI_MONTH_INDEX_MAP[monthName], day, 23, 59, 59, 999);
  }
  return null;
}

/**
 * ตรวจสอบว่าการเข้าพักนั้นยังอยู่ในช่วงเวลาปัจจุบันหรือไม่ (Active Stay vs Past Stay)
 * ปรับปรุง: นับเฉพาะผู้ที่กำลังพักอยู่ ณ ปัจจุบันจริงๆ (ไม่รวมคนในอนาคตที่ยังไม่ถึงเวลา)
 * @param {object} stay
 * @returns {boolean}
 */
export function isStayActive(stay) {
  return isStayCurrent(stay);
}

/**
 * แยกวันที่เริ่มต้นและสิ้นสุด (start date, end date) จากข้อความช่วงเวลาภาษาไทย
 * เช่น "1 มิ.ย. - 8 ก.ย. 2569", "1- 26 ก.ย. 2568", "6 – 31 ตุลาคม 2568", "8 ธ.ค. – 2 ม.ค. 2569"
 * @param {string} text - ข้อความช่วงวันที่
 * @param {number} defaultYear - ปี พ.ศ. สำรอง เช่น 2569
 * @returns {{ start: string, end: string }} รูปแบบ ISO 'YYYY-MM-DD'
 */
export function parseThaiDateRange(text, defaultYear = 2569) {
  if (!text) return { start: "", end: "" };
  const cleaned = text.replace(/–/g, "-").trim();
  const parts = cleaned.split("-");
  if (parts.length < 2) return { start: "", end: "" };

  const endStr = parts[1].trim();
  let startStr = parts[0].trim();

  const THAI_MONTHS_MAP = {
    "มกราคม": 0, "กุมภาพันธ์": 1, "มีนาคม": 2, "เมษายน": 3, "พฤษภาคม": 4, "มิถุนายน": 5,
    "กรกฎาคม": 6, "สิงหาคม": 7, "กันยายน": 8, "ตุลาคม": 9, "พฤศจิกายน": 10, "ธันวาคม": 11,
    "ม.ค": 0, "ก.พ": 1, "มี.ค": 2, "เม.ย": 3, "พ.ค": 4, "มิ.ย": 5,
    "ก.ค": 6, "ส.ค": 7, "ก.ย": 8, "ต.ค": 9, "พ.ย": 10, "ธ.ค": 11
  };

  // Find 4-digit year in endStr
  const yMatch = endStr.match(/(25\d{2}|20\d{2})/);
  let endYear = defaultYear;
  if (yMatch) {
    endYear = parseInt(yMatch[1], 10);
  } else {
    const y2Match = endStr.match(/\s+(\d{2})$/);
    if (y2Match) {
      endYear = 2500 + parseInt(y2Match[1], 10);
    }
  }

  // Find month in endStr
  let endMonth = null;
  for (const [m, idx] of Object.entries(THAI_MONTHS_MAP)) {
    if (endStr.includes(m)) {
      endMonth = idx;
      break;
    }
  }

  // Find day in endStr
  let endDay = null;
  const endNumMatches = [...endStr.matchAll(/\b(\d{1,2})\b/g)].map(m => parseInt(m[1], 10));
  for (const num of endNumMatches) {
    if (num >= 1 && num <= 31 && num !== (endYear % 100)) {
      endDay = num;
      break;
    }
  }

  // Find month in startStr
  let startMonth = null;
  for (const [m, idx] of Object.entries(THAI_MONTHS_MAP)) {
    if (startStr.includes(m)) {
      startMonth = idx;
      break;
    }
  }
  if (startMonth === null) startMonth = endMonth;

  // Find year in startStr
  let startYear = endYear;
  const startYMatch = startStr.match(/(25\d{2}|20\d{2})/);
  if (startYMatch) {
    startYear = parseInt(startYMatch[1], 10);
  } else if (startMonth !== null && endMonth !== null && startMonth > endMonth) {
    startYear = endYear - 1;
  }

  // Find day in startStr
  let startDay = null;
  const startNumMatches = [...startStr.matchAll(/\b(\d{1,2})\b/g)].map(m => parseInt(m[1], 10));
  for (const num of startNumMatches) {
    if (num >= 1 && num <= 31 && num !== (startYear % 100)) {
      startDay = num;
      break;
    }
  }

  const pad = (n) => String(n).padStart(2, "0");
  const formatIso = (y, m, d) => {
    if (!y || m === null || !d) return "";
    const adYear = y > 2400 ? y - 543 : y;
    return `${adYear}-${pad(m + 1)}-${pad(d)}`;
  };

  return {
    start: formatIso(startYear, startMonth, startDay),
    end: formatIso(endYear, endMonth, endDay)
  };
}

/**
 * แปลงวันที่เริ่มและสิ้นสุด ISO (YYYY-MM-DD) เป็นข้อความช่วงภาษาไทย
 * เช่น "1 มิ.ย. - 8 ก.ย. 2569", "1 - 26 ก.ย. 2568"
 * @param {string} startIso 
 * @param {string} endIso 
 * @returns {string}
 */
export function formatThaiDateRangeText(startIso, endIso) {
  if (!startIso) return "";
  const sParts = startIso.split("-").map(p => parseInt(p, 10));
  if (sParts.length < 3 || isNaN(sParts[0])) return "";

  const thaiMonthsShort = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
  const sYear = sParts[0] < 2400 ? sParts[0] + 543 : sParts[0];
  const sMonth = thaiMonthsShort[(sParts[1] - 1) % 12];
  const sDay = sParts[2];

  if (!endIso) {
    return `${sDay} ${sMonth} ${sYear}`;
  }

  const eParts = endIso.split("-").map(p => parseInt(p, 10));
  if (eParts.length < 3 || isNaN(eParts[0])) {
    return `${sDay} ${sMonth} ${sYear}`;
  }

  const eYear = eParts[0] < 2400 ? eParts[0] + 543 : eParts[0];
  const eMonth = thaiMonthsShort[(eParts[1] - 1) % 12];
  const eDay = eParts[2];

  if (sYear === eYear && sMonth === eMonth) {
    return `${sDay} - ${eDay} ${eMonth} ${sYear}`;
  }
  if (sYear === eYear) {
    return `${sDay} ${sMonth} - ${eDay} ${eMonth} ${sYear}`;
  }
  return `${sDay} ${sMonth} ${sYear} - ${eDay} ${eMonth} ${eYear}`;
}

export const THAI_MONTHS_FULL = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"
];

/**
 * แปลงช่วงเวลาเข้าพักเป็นข้อความภาษาไทย พ.ศ. เต็มรูปแบบ
 * รองรับทั้ง ISO (YYYY-MM-DD), ข้อความช่วงเดิม, วันที่เข้าพัก/ออก
 * ตัวอย่างผลลัพธ์:
 * - "19 ตุลาคม - 27 พฤศจิกายน 2569"
 * - "เข้าพัก: 21 ตุลาคม 2569"
 * - "ถึง 27 พฤศจิกายน 2569"
 * @param {string} rawRange 
 * @param {string} checkInDate 
 * @param {string} checkOutDate 
 * @returns {string}
 */
export function formatStayDateRangeThai(rawRange, checkInDate, checkOutDate) {
  let inDate = checkInDate ? String(checkInDate).split("T")[0].trim() : "";
  let outDate = checkOutDate ? String(checkOutDate).split("T")[0].trim() : "";
  let text = (rawRange || "").trim();

  // ฟังก์ชันย่อยแปลงวันที่ ISO เป็น พ.ศ. ภาษาไทย
  const formatIsoToThai = (isoStr) => {
    if (!isoStr) return "";
    const parts = isoStr.split("-").map((p) => parseInt(p, 10));
    if (parts.length < 3 || isNaN(parts[0])) return isoStr;
    const year = parts[0] < 2400 ? parts[0] + 543 : parts[0];
    const month = THAI_MONTHS_FULL[parts[1] - 1] || "";
    const day = parts[2];
    return `${day} ${month} ${year}`;
  };

  // 1. ถ้ามีทั้ง checkInDate และ checkOutDate ที่ถูกต้อง
  if (inDate && outDate) {
    const inParts = inDate.split("-").map((p) => parseInt(p, 10));
    const outParts = outDate.split("-").map((p) => parseInt(p, 10));
    if (inParts.length === 3 && outParts.length === 3 && !isNaN(inParts[0]) && !isNaN(outParts[0])) {
      const inYear = inParts[0] < 2400 ? inParts[0] + 543 : inParts[0];
      const outYear = outParts[0] < 2400 ? outParts[0] + 543 : outParts[0];
      const inMonth = THAI_MONTHS_FULL[inParts[1] - 1];
      const outMonth = THAI_MONTHS_FULL[outParts[1] - 1];

      if (inYear === outYear && inMonth === outMonth) {
        return `${inParts[2]} - ${outParts[2]} ${outMonth} ${outYear}`;
      } else if (inYear === outYear) {
        return `${inParts[2]} ${inMonth} - ${outParts[2]} ${outMonth} ${outYear}`;
      } else {
        return `${inParts[2]} ${inMonth} ${inYear} - ${outParts[2]} ${outMonth} ${outYear}`;
      }
    }
  }

  // 2. ถ้า text มี ISO YYYY-MM-DD หรือคำว่า "ถึง 2026-11-27" ให้แปลงเป็น พ.ศ.
  if (text) {
    // ถ้าเป็นข้อความที่มี ค.ศ. เช่น "ถึง 2026-11-27" หรือ "2026-10-19 - 2026-11-27"
    if (text.match(/20\d\d-\d{2}-\d{2}/)) {
      const isoMatches = text.match(/20\d\d-\d{2}-\d{2}/g);
      if (isoMatches && isoMatches.length === 2 && !inDate && !outDate) {
        return formatStayDateRangeThai("", isoMatches[0], isoMatches[1]);
      } else if (isoMatches && isoMatches.length === 1) {
        if (text.startsWith("ถึง") || text.includes("ถึง")) {
          return `ถึง ${formatIsoToThai(isoMatches[0])}`;
        }
        if (!inDate) inDate = isoMatches[0];
      }
    } else if (!text.match(/20\d\d/) && (text.includes("ตุลาคม") || text.includes("พฤศจิกายน") || text.includes("ธันวาคม") || text.includes("มกราคม") || text.includes("กุมภาพันธ์") || text.includes("มีนาคม") || text.includes("เมษายน") || text.includes("พฤษภาคม") || text.includes("มิถุนายน") || text.includes("กรกฎาคม") || text.includes("สิงหาคม") || text.includes("กันยายน") || text.includes("ต.ค.") || text.includes("พ.ย.") || text.includes("ธ.ค.") || text.includes("ม.ค.") || text.includes("ก.พ.") || text.includes("มี.ค.") || text.includes("เม.ย.") || text.includes("พ.ค.") || text.includes("มิ.ย.") || text.includes("ก.ค.") || text.includes("ส.ค.") || text.includes("ก.ย."))) {
      // เป็นภาษาไทย พ.ศ. อยู่แล้ว
      return text;
    }
  }

  // 3. ถ้ามีเฉพาะ checkInDate (เช่น บุคลากร รพ. ที่เข้าพักต่อเนื่อง)
  if (inDate && !outDate) {
    return `เข้าพัก: ${formatIsoToThai(inDate)}`;
  }

  // 4. ถ้ามีเฉพาะ checkOutDate
  if (!inDate && outDate) {
    return `ถึง ${formatIsoToThai(outDate)}`;
  }

  return text || "-";
}

/**
 * ตรวจสอบสถานะช่วงเวลาการฝึกอบรมของผู้ฝึกอบรม
 * - ACTIVE: อยู่ในช่วงเวลา (กำลังฝึกงาน/ฝึกอบรม)
 * - UPCOMING: คนในอนาคตที่ใส่มาก่อน (รอเริ่มฝึก)
 * - PAST: พ้นช่วงเวลา/เกินแล้ว (สำเร็จการฝึก/ออกไปแล้ว)
 * @param {object} item
 * @param {Date} customNow
 * @returns {{ key: "ACTIVE"|"UPCOMING"|"PAST", label: string, color: string }}
 */
export function getTraineeTrainingPeriodStatus(item, customNow = new Date()) {
  if (!item) return { key: "PAST", label: "พ้นช่วงเวลา/เกินแล้ว", color: "secondary" };
  const now = new Date(customNow);

  // 1. ถ้ามี statusKey หรือ status ระบุว่าสำเร็จการฝึกอบรม หรือ ยกเลิก
  if (item.statusKey === 3 || item.statusKey === 4) {
    return { key: "PAST", label: "พ้นช่วงเวลา/เกินแล้ว", color: "secondary" };
  }
  const stText = (item.statusText || item.status || "").trim();
  if (stText.includes("สำเร็จ") || stText.includes("ออกไปแล้ว") || stText.includes("ยกเลิก") || stText.includes("พ้นกำหนด")) {
    return { key: "PAST", label: "พ้นช่วงเวลา/เกินแล้ว", color: "secondary" };
  }

  // 2. ตรวจสอบจาก startDate และ endDate
  let start = null;
  let end = null;

  if (item.startDate) {
    const s = new Date(item.startDate);
    if (!isNaN(s.getTime())) {
      start = s;
      start.setHours(0, 0, 0, 0);
    }
  }

  const rawEnd = item.endDate || item.checkOutDate;
  if (rawEnd) {
    const e = new Date(rawEnd);
    if (!isNaN(e.getTime())) {
      end = e;
      end.setHours(23, 59, 59, 999);
    }
  }

  // ถ้าไม่มี end จาก ISO ให้ลอง parse จาก dateRange
  if (!end && item.dateRange) {
    const parsedEnd = parseThaiEndDate(item.dateRange, item.fiscalYear);
    if (parsedEnd) {
      end = parsedEnd;
      end.setHours(23, 59, 59, 999);
    }
  }

  if (start && end) {
    if (now < start) return { key: "UPCOMING", label: "รอเริ่มฝึก (คนในอนาคต)", color: "info" };
    if (now > end) return { key: "PAST", label: "พ้นช่วงเวลา/เกินแล้ว", color: "secondary" };
    return { key: "ACTIVE", label: "กำลังฝึก (อยู่ในช่วงเวลา)", color: "success" };
  } else if (end) {
    if (now > end) return { key: "PAST", label: "พ้นช่วงเวลา/เกินแล้ว", color: "secondary" };
    return { key: "ACTIVE", label: "อยู่ในช่วงเวลา", color: "success" };
  } else if (start) {
    if (now < start) return { key: "UPCOMING", label: "รอเริ่มฝึก (คนในอนาคต)", color: "info" };
    return { key: "ACTIVE", label: "อยู่ในช่วงเวลา", color: "success" };
  }

  // 3. Fallback จาก status string
  if (stText.includes("รอ") || stText.includes("ยังไม่") || item.statusKey === 1) {
    return { key: "UPCOMING", label: "รอเริ่มฝึก (คนในอนาคต)", color: "info" };
  }

  return { key: "ACTIVE", label: "อยู่ในช่วงเวลา (กำลังฝึก)", color: "success" };
}

/**
 * ดึงวันที่เริ่มต้นและสิ้นสุด (ISO format 'YYYY-MM-DD') ของการเข้าพัก
 * รองรับทั้ง checkInDate/checkOutDate, startDate/endDate, และการ parse จากข้อความ dateRange ภาษาไทย
 * @param {object} stay 
 * @returns {{ start: string, end: string }}
 */
export function getStayDateRange(stay) {
  if (!stay) return { start: "", end: "" };
  let start = (stay.checkInDate || stay.CheckInDate || stay.startDate || stay.StartDate || "").toString().split("T")[0];
  let end = (stay.checkOutDate || stay.CheckOutDate || stay.endDate || stay.EndDate || "").toString().split("T")[0];

  if ((!start || !end) && (stay.dateRange || stay.StayDateRangeText || stay.dateRangeText)) {
    const text = stay.dateRange || stay.StayDateRangeText || stay.dateRangeText;
    const year = stay.fiscalYear || stay.FiscalYear || 2569;
    const parsed = parseThaiDateRange(text, year);
    if (!start && parsed.start) start = parsed.start;
    if (!end && parsed.end) end = parsed.end;
  }
  return { start, end };
}

/**
 * ตรวจสอบว่าช่วงเวลาสองช่วง [startA, endA] และ [startB, endB] ซ้อนทับกัน (ชนกัน) หรือไม่
 * @param {string} startA - วันเริ่มต้นช่วง A ('YYYY-MM-DD')
 * @param {string} endA - วันสิ้นสุดช่วง A ('YYYY-MM-DD')
 * @param {string} startB - วันเริ่มต้นช่วง B ('YYYY-MM-DD')
 * @param {string} endB - วันสิ้นสุดช่วง B ('YYYY-MM-DD')
 * @returns {boolean}
 */
export function checkDateRangesOverlap(startA, endA, startB, endB) {
  if (!startA || !endA || !startB || !endB) {
    let sA = startA || endA;
    let eA = endA || startA;
    let sB = startB || endB;
    let eB = endB || startB;
    if (!sA || !sB) return false;
    return sA <= eB && eA >= sB;
  }
  return startA <= endB && endA >= startB;
}

/**
 * วิเคราะห์สถานะช่วงเวลาของการเข้าพัก (Timeline Status)
 * คืนค่า object:
 * - key: "CURRENT" (พักอยู่ปัจจุบัน วันนี้อยู่ในช่วงเวลา), "FUTURE" (ในอนาคต/ไม่ใช่ตอนนี้), "PAST" (ออกไปแล้ว/พ้นวันฝึก)
 * @param {object} stay
 * @param {Date} [customNow]
 * @returns {{ key: "CURRENT" | "FUTURE" | "PAST", label: string, badgeText: string, color: string }}
 */
export function getStayTimelineStatus(stay, customNow = new Date()) {
  if (!stay) return { key: "PAST", label: "ไม่พบข้อมูล", badgeText: "ไม่พบข้อมูล", color: "secondary" };

  const now = new Date(customNow);
  now.setHours(12, 0, 0, 0);

  const { start: startStr, end: endStr } = getStayDateRange(stay);

  let startDate = null;
  let endDate = null;

  if (startStr) {
    const s = new Date(startStr);
    if (!isNaN(s.getTime())) {
      s.setHours(0, 0, 0, 0);
      startDate = s;
    }
  }

  if (endStr) {
    const e = new Date(endStr);
    if (!isNaN(e.getTime())) {
      e.setHours(23, 59, 59, 999);
      endDate = e;
    }
  }

  // 0. ตรวจสอบสถานะการยกเลิกก่อนเป็นอันดับแรก (หากยกเลิกแล้ว ไม่นับเป็นผู้เข้าพักทั้งปัจจุบันและอนาคต)
  const remarks = (stay.remarks || stay.Remarks || "").toLowerCase();
  const paymentStatus = (stay.paymentStatus || stay.PaymentStatus || "").toUpperCase();
  const statusStr = (stay.status || stay.Status || "").toLowerCase();
  const isExplicitlyCancelled =
    stay.isActive === false ||
    stay.IsActive === false ||
    remarks.includes("ยกเลิก") ||
    statusStr.includes("ยกเลิก") ||
    paymentStatus === "CANCELLED";

  if (isExplicitlyCancelled) {
    return {
      key: "CANCELLED",
      label: "ยกเลิกการจองแล้ว",
      badgeText: "❌ ยกเลิกแล้ว",
      color: "secondary"
    };
  }

  // หากเป็นช่วงอนาคต แต่ถูกปลดสถานะออก (isCurrentStay === false) ถือว่ายกเลิกการจอง
  if (startDate && now < startDate && (stay.isCurrentStay === false || stay.IsCurrentStay === false)) {
    return {
      key: "CANCELLED",
      label: "ยกเลิกการจองแล้ว",
      badgeText: "❌ ยกเลิกแล้ว",
      color: "secondary"
    };
  }

  // 1. ตรวจสอบตามวันที่ก่อนเป็นอันดับแรก
  if (startDate && endDate) {
    if (now < startDate) {
      return {
        key: "FUTURE",
        label: "ในอนาคต (ไม่ใช่ตอนนี้)",
        badgeText: "⏳ พักในอนาคต (ยังไม่ถึงเวลา)",
        color: "info"
      };
    }
    if (now > endDate) {
      return {
        key: "PAST",
        label: "ออกไปแล้ว (พ้นวันฝึก)",
        badgeText: "🚪 ออกไปแล้ว",
        color: "secondary"
      };
    }
    return {
      key: "CURRENT",
      label: "กำลังพักอาศัย (ปัจจุบัน)",
      badgeText: "🟢 กำลังพักอยู่",
      color: "success"
    };
  } else if (endDate) {
    if (now > endDate) {
      return { key: "PAST", label: "ออกไปแล้ว (พ้นวันฝึก)", badgeText: "🚪 ออกไปแล้ว", color: "secondary" };
    }
    return { key: "CURRENT", label: "กำลังพักอาศัย (ปัจจุบัน)", badgeText: "🟢 กำลังพักอยู่", color: "success" };
  } else if (startDate) {
    if (now < startDate) {
      return { key: "FUTURE", label: "ในอนาคต (ไม่ใช่ตอนนี้)", badgeText: "⏳ พักในอนาคต (ยังไม่ถึงเวลา)", color: "info" };
    }
    return { key: "CURRENT", label: "กำลังพักอาศัย (ปัจจุบัน)", badgeText: "🟢 กำลังพักอยู่", color: "success" };
  }

  // 2. Fallback จาก status string หรือ flag เมื่อไม่มีข้อมูลวันที่
  const st = (stay.status || "").trim();
  if (st.includes("รอ") || st.includes("อนาคต") || st.includes("จอง")) {
    return { key: "FUTURE", label: "ในอนาคต (ไม่ใช่ตอนนี้)", badgeText: "⏳ พักในอนาคต (ยังไม่ถึงเวลา)", color: "info" };
  }
  if (
    st === "ประวัติการพัก" ||
    st === "ออกไปแล้ว" ||
    st === "สิ้นสุดการพัก" ||
    st === "พ้นกำหนด" ||
    stay.isCurrentStay === false ||
    stay.IsCurrentStay === false ||
    stay.isCurrentStay === 0 ||
    stay.IsCurrentStay === 0
  ) {
    return { key: "PAST", label: "ออกไปแล้ว (พ้นวันฝึก)", badgeText: "🚪 ออกไปแล้ว", color: "secondary" };
  }
  if (st === "กำลังพักอาศัย" || stay.isCurrentStay === true || stay.IsCurrentStay === true) {
    return { key: "CURRENT", label: "กำลังพักอาศัย (ปัจจุบัน)", badgeText: "🟢 กำลังพักอยู่", color: "success" };
  }

  return { key: "CURRENT", label: "กำลังพักอาศัย (ปัจจุบัน)", badgeText: "🟢 กำลังพักอยู่", color: "success" };
}

/**
 * ตรวจสอบว่าเป็นผู้เข้าพักปัจจุบันหรือไม่ (วันเวลาอยู่ในช่วงปัจจุบันจริงๆ startDate <= now <= endDate)
 * จะไม่นับคนที่เข้าพักในอนาคต
 */
export function isStayCurrent(stay) {
  return getStayTimelineStatus(stay).key === "CURRENT";
}

/**
 * ตรวจสอบว่าเป็นผู้เข้าพักในอนาคตหรือไม่ (startDate > now - ยังไม่ถึงเวลา ไม่ใช่ตอนนี้)
 */
export function isStayFuture(stay) {
  return getStayTimelineStatus(stay).key === "FUTURE";
}

/**
 * ตรวจสอบว่าเป็นผู้เข้าพักที่ออกไปแล้วหรือไม่ (endDate < now)
 */
export function isStayPast(stay) {
  return getStayTimelineStatus(stay).key === "PAST";
}

/**
 * คำนวณค่าใช้จ่ายสำหรับการย้ายออก (Check-out) ตามเวลาที่อยู่จริง
 * รองรับการคำนวณจากวันที่เข้าถึงวันนี้ หรือระบุจำนวนเดือน/วันเอง
 * @param {object} params
 * @returns {object}
 */
export function calculateCheckoutFee({
  building = "ตึก 1",
  checkInDate = "",
  checkOutDate = "",
  isManualDuration = false,
  customMonths = 0,
  customDays = 0,
  calculationMode = "DAILY", // "DAILY", "MONTHLY", "PRORATED"
  dailyRate = 100,
  monthlyRate = 500,
  paidAmount = 0
}) {
  let elapsedDays = 0;
  let elapsedMonths = 0;
  let remainingDays = 0;

  if (isManualDuration) {
    elapsedMonths = Math.max(0, parseInt(customMonths, 10) || 0);
    remainingDays = Math.max(0, parseInt(customDays, 10) || 0);
    elapsedDays = elapsedMonths * 30 + remainingDays;
  } else {
    // คำนวณจากวันที่เข้าและวันที่ออก (วันนี้)
    if (checkInDate && checkOutDate) {
      const s = new Date(checkInDate);
      const e = new Date(checkOutDate);
      if (!isNaN(s.getTime()) && !isNaN(e.getTime())) {
        s.setHours(0, 0, 0, 0);
        e.setHours(0, 0, 0, 0);
        const diffMs = e.getTime() - s.getTime();
        // นับรวมวันเข้าและวันออกอย่างน้อย 1 วัน
        const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1;
        elapsedDays = Math.max(1, diffDays);

        elapsedMonths = Math.floor(elapsedDays / 30);
        remainingDays = elapsedDays % 30;
      }
    }
  }

  let totalFee = 0;
  let formulaText = "";

  if (calculationMode === "DAILY") {
    const dRate = Number(dailyRate) || 100;
    const daysToCharge = isManualDuration ? (elapsedMonths * 30 + remainingDays) : elapsedDays;
    totalFee = daysToCharge * dRate;
    formulaText = `${daysToCharge} วัน × ${dRate} บาท/วัน = ${totalFee.toLocaleString()} บาท`;
  } else if (calculationMode === "MONTHLY") {
    const mRate = Number(monthlyRate) || 500;
    const dRate = Number(dailyRate) || 100;
    const monthCharge = elapsedMonths * mRate;
    const dayCharge = remainingDays * dRate;
    totalFee = monthCharge + dayCharge;
    const parts = [];
    if (elapsedMonths > 0) parts.push(`${elapsedMonths} เดือน × ${mRate} บ. (${monthCharge.toLocaleString()} บ.)`);
    if (remainingDays > 0) parts.push(`${remainingDays} วัน × ${dRate} บ. (${dayCharge.toLocaleString()} บ.)`);
    if (parts.length === 0) parts.push(`0 วัน (0 บ.)`);
    formulaText = parts.join(" + ") + ` = ${totalFee.toLocaleString()} บาท`;
  } else {
    // PRORATED ตามสูตร calculateDormFeeByDays
    const daysToCharge = isManualDuration ? (elapsedMonths * 28 + remainingDays) : elapsedDays;
    const prorated = calculateDormFeeByDays(building, daysToCharge);
    totalFee = prorated.totalAmount;
    formulaText = prorated.breakdownText + ` = ${totalFee.toLocaleString()} บาท`;
  }

  const paid = Number(paidAmount) || 0;
  const netBalance = totalFee - paid;
  const remainingToPay = Math.max(0, netBalance);
  const refundAmount = Math.max(0, -netBalance);

  let balanceStatus = "SETTLED";
  let statusText = "ชำระครบถ้วนพอดี";
  let alertColor = "success";

  if (remainingToPay > 0) {
    balanceStatus = "UNDERPAID";
    statusText = `ค้างชำระเพิ่ม ${remainingToPay.toLocaleString()} บาท`;
    alertColor = "danger";
  } else if (refundAmount > 0) {
    balanceStatus = "OVERPAID";
    statusText = `ต้องคืนเงินส่วนต่าง ${refundAmount.toLocaleString()} บาท`;
    alertColor = "info";
  }

  return {
    elapsedDays,
    elapsedMonths,
    remainingDays,
    totalFee,
    formulaText,
    paidAmount: paid,
    remainingToPay,
    refundAmount,
    balanceStatus,
    statusText,
    alertColor
  };
}
